import React, { useEffect } from "react";
import { Redirect, useParams } from "react-router-dom";
import { Button, Form, Input, message } from "antd";
import {
  setPasssword,
  setRepeatPassword,
  changePassword,
} from "../../../application/change-password-slice";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { logger } from "../../../utils";
import { AuthForm, DocumentTitle } from "../../components";
import { useQuery } from "../../hooks";
import I18n from "../../i18n";
import { resetLoginAuth } from "../../../application/login-auth-slice";

const { Item, useForm } = Form;

type RouteParams = { phone: string; token: string };

export default function ChangePassword() {
  const { phone, token } = useParams<RouteParams>();
  const query = useQuery();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const {
    password,
    loading,
    repeatPassword,
    passwordValidationStatus,
    hasSubmitted,
  } = useAppSelector((state) => state.changePassword);

  const submit = async () => {
    try {
      await form.validateFields();
      dispatch(changePassword(phone, token));
    } catch (error) {
      logger.error(error);
    }
  };

  const getPasswordErrorText = (): string | undefined => {
    if (!hasSubmitted) return undefined;
    if (password === "") return I18n.general.passwordErrors.empty;
    if (passwordValidationStatus === "valid") return undefined;
    if (passwordValidationStatus === "short")
      return I18n.general.passwordErrors.short;
  };

  const getPasswordValidationStatus = (): "error" | undefined => {
    if (!hasSubmitted) return undefined;
    if (passwordValidationStatus === "repeatPasswordNoMatch") return undefined;
    if (passwordValidationStatus !== "valid") return "error";
    return undefined;
  };

  const getRepeatPasswordErrorText = (): string | undefined => {
    if (!hasSubmitted || getPasswordValidationStatus() !== undefined)
      return undefined;
    if (repeatPassword === "")
      return I18n.general.passwordErrors.repeatPasswordEmpty;
    if (passwordValidationStatus === "valid") return undefined;
    if (passwordValidationStatus === "repeatPasswordNoMatch")
      return I18n.general.passwordErrors.repeatPasswordDontMatch;
  };

  const getRepeatPasswordValidationStatus = (): "error" | undefined => {
    if (!hasSubmitted) return undefined;
    if (passwordValidationStatus === "repeatPasswordNoMatch") return "error";
    return undefined;
  };

  useEffect(() => {
    if (loading === "failed") message.error(I18n.general.networkError);
  }, [loading]);

  if (loading === "succeeded") {
    const back = query.get("back");
    dispatch(resetLoginAuth());
    return <Redirect to={`/login-auth/${phone}?back=${back}`} />;
  }

  const isLoading = loading === "pending";
  return (
    <DocumentTitle title={I18n.chanePassword.title}>
      <AuthForm
        title={I18n.title}
        subtitle={I18n.chanePassword.title}
        description={I18n.chanePassword.description}
      >
        <Form
          form={form}
          // TODO: add this for other pages
          onFinish={submit}
          fields={[
            { name: "password", value: password },
            { name: "repeat-password", value: repeatPassword },
          ]}
        >
          <Item
            name="password"
            help={getPasswordErrorText()}
            validateStatus={getPasswordValidationStatus()}
          >
            <Input.Password
              placeholder={I18n.general.password}
              className={
                password === "" ? "" : "login-auth-form-password-input"
              }
              disabled={isLoading}
              spellCheck={false}
              onChange={(value) => {
                dispatch(setPasssword(value.target.value));
                form.setFields([{ name: "password", value: password }]);
              }}
            />
          </Item>
          <Item
            name="repeat-password"
            help={getRepeatPasswordErrorText()}
            validateStatus={getRepeatPasswordValidationStatus()}
          >
            <Input.Password
              className={
                repeatPassword === "" ? "" : "register-form-password-input"
              }
              disabled={isLoading}
              placeholder={I18n.general.repeatPassword}
              spellCheck={false}
              onChange={(value) => {
                dispatch(setRepeatPassword(value.target.value));
                form.setFields([
                  { name: "repeat-password", value: repeatPassword },
                ]);
              }}
              required={true}
            />
          </Item>
          <Item>
            <Button type="primary" block onClick={submit} loading={isLoading}>
              {I18n.chanePassword.submit}
            </Button>
          </Item>
        </Form>
      </AuthForm>
    </DocumentTitle>
  );
}
